import './TeamsScreen.less';

import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Dropdown,
  Spin,
  Tooltip,
  Select,
} from 'antd';
import { ItemType } from 'antd/es/menu/interface';
import { MoreOutlined } from '@ant-design/icons';
import { ColumnsType } from 'antd/es/table';
import { Notify } from '../../services/notificationService';
import { AppDispatch, RootState } from '../../app/store';
import { Team, TeamWithSubTeams } from '../../interfaces/team';
import { AppRoutes, getTeamRoute } from '../../constants/route';
import { generateStringSorter } from '../../utils/sorters';
import {
  createTeam,
  deleteTeam,
  renameTeam,
  selectAccount,
} from '../../app/ActionTypes/teamActionTypes';
import ActionError from '../../utils/ActionError';
import { Resource } from '../../enums/common';
import { defineAbilityFor } from '../../services/abilityService';
import RoleBadge from '../../components/RoleBadge/RoleBadge';
import {
  getTeamName,
  groupTeams,
  // validateParentTeamLicense,
} from '../../services/account.service';
import { confirmAction } from '../../utils/confirmActionModal';
import InfoTooltip from '../../components/InfoTooltip/InfoTooltip';
import UserRoles from '../../enums/userRoles';
import { navigateTo } from '../../utils/routeUtils';

interface TeamsScreenProps {
  isCreatingTeam?: boolean;
}

type HierarchicalTeam = Team & {
  children?: Team[];
};

const TeamsScreen: React.FC<TeamsScreenProps> = ({
  isCreatingTeam = false,
}: TeamsScreenProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const teamState = useSelector((state: RootState) => state.team);
  const {
    allTeams: allTeamsInfo,
    newTeam: newTeamInfo,
    deletedTeamsById,
    renamedTeamsById,
  } = teamState;
  const [isModalVisible, setIsModalVisible] = useState<boolean>(isCreatingTeam);
  const [isRenameModalVisible, setIsRenameModalVisible] =
    useState<boolean>(false);
  const [teamToRename, setTeamToRename] = useState<Team | null>(null);
  /*
  const [isValidatingParentTeam, setIsValidatingParentTeam] =
    useState<boolean>(false);
      */
  const [form] = Form.useForm();
  const [renameForm] = Form.useForm();

  useEffect(() => {
    setIsModalVisible(isCreatingTeam);
  }, [isCreatingTeam]);

  const masterTeamOptions = useMemo(() => {
    const nonSubTeams = allTeamsInfo.records.filter(
      (team) =>
        !team.master_organization_id &&
        team.current_user?.role === UserRoles.OWNER
    );
    return nonSubTeams.map((team) => ({
      value: team.id,
      label: getTeamName(team),
    }));
  }, [allTeamsInfo.records]);

  // Transform teams into hierarchical structure
  const hierarchicalTeams = useMemo((): HierarchicalTeam[] => {
    const groupedTeams = groupTeams(allTeamsInfo.records) as TeamWithSubTeams[];
    if (!groupedTeams) {
      return [];
    }
    return groupedTeams.map((t: TeamWithSubTeams): HierarchicalTeam => {
      return {
        ...t,
        children: t.subTeams,
      };
    });
  }, [allTeamsInfo.records]);

  const validateUniqueTeamName = (
    value: string,
    excludeTeamId?: string
  ): Promise<void> => {
    if (!value) {
      return Promise.resolve();
    }
    const trimmedValue = value.trim().toLowerCase();
    const existingTeam = allTeamsInfo.records.find((team) => {
      const isSameTeam =
        excludeTeamId !== undefined && team.id === excludeTeamId;
      return !isSameTeam && getTeamName(team).toLowerCase() === trimmedValue;
    });
    if (existingTeam) {
      return Promise.reject(new Error('A team with this name already exists'));
    }
    return Promise.resolve();
  };

  /*
  const validateParentTeamLicenseField = async (
    value: string | undefined
  ): Promise<void> => {
    if (!value) {
      return Promise.resolve();
    }

    setIsValidatingParentTeam(true);
    try {
      const hasLicense = await validateParentTeamLicense(
        value,
        allTeamsInfo.records
      );
      if (!hasLicense) {
        return Promise.reject(
          new Error(
            'This parent team has no license left. Please update its subscription or choose another team'
          )
        );
      }
      return Promise.resolve();
    } catch (error) {
      return Promise.reject(
        new Error('Failed to validate parent team license. Please try again.')
      );
    } finally {
      setIsValidatingParentTeam(false);
    }
  };
  */

  const showModal = () => {
    setIsModalVisible(true);
    // setIsValidatingParentTeam(false);
    form.resetFields();
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    // setIsValidatingParentTeam(false);
    navigateTo({
      url: AppRoutes.teamsSettings.getUrl(),
      navigate,
    });
  };

  const handleRenameCancel = () => {
    setIsRenameModalVisible(false);
    setTeamToRename(null);
    renameForm.resetFields();
  };

  const handleCreateTeam = async (values: {
    name: string;
    master_organization_id?: string;
  }) => {
    try {
      const newTeamPayload = {
        name: values.name.trim(),
        master_organization_id: values.master_organization_id || null,
      };
      const newTeam = (await dispatch(createTeam(newTeamPayload)).unwrap())
        .data;
      Notify.success(
        `Team '${newTeam.cirra_organization_name}' has been created`
      );

      setIsModalVisible(false);

      // Select the new team and wait for all state updates and data fetching to complete
      await dispatch(selectAccount(newTeam)).unwrap();

      // Navigate to home page (MCP) - state is now properly synchronized
      navigate('/');
    } catch (error) {
      Notify.error((error as Error).message || 'Failed to create team');
      navigateTo({
        url: AppRoutes.teamsSettings.getUrl(),
        navigate,
      });
    }
  };

  const handleRenameTeam = async (values: { name: string }) => {
    if (!teamToRename) return;

    try {
      await dispatch(
        renameTeam({ team: teamToRename, newName: values.name.trim() })
      ).unwrap();
      Notify.success(`Team has been renamed to '${values.name.trim()}'`);
      setIsRenameModalVisible(false);
      setTeamToRename(null);
      renameForm.resetFields();
    } catch (error) {
      Notify.error((error as Error).message || 'Failed to rename team');
    }
  };

  const getActionItems = (team: Team): ItemType[] => {
    if (!team.current_user) {
      return [];
    }

    // Cannot use `{teamAbility} = useAbility()`
    // because we need the ability of the target team user,
    // not the currently selected user
    const ability = defineAbilityFor(team.current_user);
    const canDeleteTeam = ability.can('delete', Resource.UserInfoAsOrg, team);
    const canUpdateTeam = ability.can('update', Resource.UserInfoAsOrg, team);
    return [
      {
        key: 'rename',
        label: (
          <Tooltip
            title={
              canUpdateTeam
                ? ''
                : 'You do not have permission to rename this team'
            }
          >
            Rename team
          </Tooltip>
        ),
        disabled: !canUpdateTeam,
        onClick: () => {
          setTeamToRename(team);
          renameForm.setFieldsValue({
            name: team.cirra_organization_name || '',
          });
          setIsRenameModalVisible(true);
        },
      },
      {
        key: 'manage-users',
        label: 'Manage users',
        onClick: async () => {
          await dispatch(selectAccount(team));
          navigate(getTeamRoute(team.id).children.accessUsers.path);
        },
      },
      {
        key: 'delete',
        label: (
          <Tooltip
            title={
              canDeleteTeam
                ? ''
                : 'You do not have permission to delete this team'
            }
          >
            Delete team
          </Tooltip>
        ),
        danger: true,
        disabled: !canDeleteTeam,
        onClick: async () => {
          try {
            const confirmedDeletion = await confirmAction({
              title: `Delete Team ${getTeamName(team)}`,
              content: (
                <span>
                  Are you sure you want to delete this team?
                  <br />
                  {!team.master_organization_id &&
                    `Its sub teams (if any) will be downgraded to free plan.`}
                </span>
              ),
              okText: 'Delete',
              okType: 'danger',
              cancelText: 'Cancel',
            });
            if (!confirmedDeletion) {
              return;
            }

            await dispatch(deleteTeam({ team })).unwrap();
            Notify.success(
              `Team '${team.cirra_organization_name || team.cirra_organization_id}' has been deleted`
            );

            // If we deleted the current team, switch to personal account
            const currentTeamId = teamState.currentTeam.record?.id;
            if (currentTeamId === team.id) {
              await dispatch(selectAccount(null)).unwrap();
            }

            // If the deleted team is the master team of the current team, reload page
            const currentTeamMasterId =
              teamState.currentTeam.record?.master_organization_id;
            if (currentTeamMasterId === team.id) {
              window.location.reload();
            }
          } catch (error: unknown) {
            const errorMessage =
              (error as ActionError)?.statusCode === 500
                ? 'Failed to delete team'
                : (error as Error).message;
            Notify.error(errorMessage);
          }
        },
      },
    ];
  };

  const columns: ColumnsType<TeamWithSubTeams> = [
    {
      title: 'ID',
      width: 250,
      dataIndex: 'cirra_organization_id',
      key: 'cirra_organization_id',
      sorter: generateStringSorter('cirra_organization_id'),
    },

    {
      title: 'Name',
      dataIndex: 'cirra_organization_name',
      key: 'cirra_organization_name',
      sorter: generateStringSorter('cirra_organization_name'),
      render: (__, team) => getTeamName(team),
    },
    {
      title: 'Role',
      width: 100,
      dataIndex: ['current_user', 'role'],
      key: 'current_user__role',
      render: (__, team) => <RoleBadge role={team.current_user?.role} />,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 75,
      render: (__, team) => {
        const isDeleting = deletedTeamsById[team.id]?.loading;
        const isRenaming = renamedTeamsById[team.id]?.loading;

        const actionItems = getActionItems(team);
        const teamAccessible = !!team.current_user;

        return (
          <div className="action-items">
            {isDeleting || isRenaming ? (
              <Spin size="small" />
            ) : (
              <Tooltip
                title={!teamAccessible && 'You do not have access to this team'}
              >
                <Dropdown
                  disabled={!teamAccessible}
                  menu={{
                    items: actionItems,
                  }}
                  trigger={['click']}
                >
                  <MoreOutlined
                    className="dots-icon"
                    onClick={(e) => e.stopPropagation()}
                  />
                </Dropdown>
              </Tooltip>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <div id="teams-screen">
      <div className="teams-screen-header">
        <div className="overview">
          <span className="overview-title">Overview</span>
        </div>
        <Button
          type="primary"
          onClick={showModal}
          loading={newTeamInfo.loading}
          disabled={newTeamInfo.loading || allTeamsInfo.loading}
        >
          + Create New Team
        </Button>
      </div>
      <Table
        dataSource={hierarchicalTeams}
        rowKey={(record) => record.id}
        loading={allTeamsInfo.loading}
        columns={columns}
        pagination={false}
        expandable={{
          defaultExpandAllRows: true,
          indentSize: 32,
          rowExpandable: (record) =>
            Boolean(record.children && record.children.length > 0),
        }}
      />
      <Modal
        title="Create New Team"
        open={isModalVisible}
        onCancel={handleCancel}
        footer={null}
        className="new-team-modal"
      >
        {newTeamInfo.loading ? <div className="linear-loading"></div> : null}
        <Form
          form={form}
          onFinish={handleCreateTeam}
          layout="vertical"
          data-hs-do-not-collect="true"
        >
          <Form.Item
            name="name"
            label="Name"
            rules={[
              { required: true, message: 'Please input name' },
              {
                validator: (_, value) => validateUniqueTeamName(value),
              },
            ]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            label={
              <span>
                Parent Team{' '}
                <InfoTooltip content="Optionally select a parent team. Sub-teams consume the credits of their parent team." />
              </span>
            }
          >
            <Tooltip
              title={
                !masterTeamOptions.length && 'You are not the owner of any team'
              }
            >
              <Form.Item
                name="master_organization_id"
                className="parent-team-select-form-item"
                /*
                rules={[
                  {
                    validator: (_, value) =>
                      validateParentTeamLicenseField(value),
                  },
                ]}
                */
              >
                <Select
                  placeholder="Select a parent team (optional)"
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  options={masterTeamOptions}
                  disabled={!masterTeamOptions.length}
                  // disabled={!masterTeamOptions.length || isValidatingParentTeam}
                  // loading={isValidatingParentTeam}
                />
              </Form.Item>
              {/*
              {isValidatingParentTeam && (
                <div>Checking remaining licenses...</div>
              )}
              */}
            </Tooltip>
          </Form.Item>

          <Form.Item className="new-team-submit-button" shouldUpdate>
            {() => {
              const areFieldsValid = !form
                .getFieldsError()
                .some(({ errors }) => errors.length);
              return (
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={newTeamInfo.loading}
                  disabled={
                    newTeamInfo.loading ||
                    allTeamsInfo.loading ||
                    !areFieldsValid
                  }
                >
                  Create Team
                </Button>
              );
            }}
          </Form.Item>
        </Form>
      </Modal>
      <Modal
        title="Rename Team"
        open={isRenameModalVisible}
        onCancel={handleRenameCancel}
        footer={null}
        className="rename-team-modal"
      >
        {teamToRename && renamedTeamsById[teamToRename.id]?.loading ? (
          <div className="linear-loading"></div>
        ) : null}
        <Form
          form={renameForm}
          onFinish={handleRenameTeam}
          layout="vertical"
          data-hs-do-not-collect="true"
        >
          <Form.Item
            name="name"
            label="Name"
            rules={[
              { required: true, message: 'Please input name' },
              {
                validator: (_, value) =>
                  validateUniqueTeamName(value, teamToRename?.id),
              },
            ]}
          >
            <Input />
          </Form.Item>
          <Form.Item className="rename-team-submit-button" shouldUpdate>
            {() => {
              const areFieldsValid = !renameForm
                .getFieldsError()
                .some(({ errors }) => errors.length);
              const isRenaming =
                !!teamToRename && !!renamedTeamsById[teamToRename.id]?.loading;
              return (
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={isRenaming}
                  disabled={isRenaming || !areFieldsValid}
                >
                  Rename Team
                </Button>
              );
            }}
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default TeamsScreen;
